import { useCallback, useRef, useState } from "react";
import { settingsType, useSettings } from "./settings";

export enum listSearchKey {
  KINDRED_DEALS = "kindredDeals",
  KINDRED_COUPONS = "kindredCoupons",
  KINDRED_OFFERS = "kindredOffers",
  KINDRED_BRANDS = "kindredBrands",
}

interface IListSearchEntry {
  page: number;
}

type ListSearchMemory = Record<string, IListSearchEntry>;

const parse = (raw: string): ListSearchMemory => {
  try {
    const parsed = JSON.parse(raw || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Remembers that a list page ran a search, so returning from a detail page
 * re-runs it instead of showing an empty list. Filters already persist in
 * settings; this only records that they were actually searched, and on which page.
 */
export const useListSearchMemory = (key: listSearchKey) => {
  const [raw, setRaw] = useSettings(settingsType.LIST_SEARCH_MEMORY);

  const rawRef = useRef(raw);
  rawRef.current = raw;

  // Read once at mount: later writes must not re-trigger a restore.
  const [restored] = useState<IListSearchEntry | undefined>(
    () => parse(raw)[key]
  );

  const rememberSearch = useCallback(
    (page: number) => {
      setRaw(JSON.stringify({ ...parse(rawRef.current), [key]: { page } }));
    },
    [key, setRaw]
  );

  const forgetSearch = useCallback(() => {
    const next = parse(rawRef.current);
    delete next[key];
    setRaw(JSON.stringify(next));
  }, [key, setRaw]);

  return {
    hasPreviousSearch: restored !== undefined,
    initialPage: restored?.page ?? 1,
    rememberSearch,
    forgetSearch,
  };
};
